/**
 * Berechtigungen-Selbsttest (Admin).
 * 
 * Diagnose-Seite, die alle User-Permissions-Zuweisungen gegen die System-Rollen
 * prüft und Inkonsistenzen meldet:
 *   - SCOPE_MISMATCH     (kritisch) — stored scope ≠ role scope
 *   - MISSING_LOCATION_IDS / MISSING_BRANCH_IDS (kritisch)
 *   - ORPHAN_LOCATION / ORPHAN_BRANCH (warnung)
 *   - UNKNOWN_ROLE (kritisch)
 *   - ORPHAN_USER (warnung)
 * 
 * Bietet einen „Reparieren"-Button, der SCOPE_MISMATCH-Fälle heilt.
 */
import React, { useState, useEffect } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import {
  Shield, RefreshCw, AlertTriangle, AlertCircle, Info, CheckCircle2, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import { permissionsAuditAPI } from '../lib/api';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const SEVERITY_CONFIG = {
  critical: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-red-200 dark:border-red-900', label: 'Kritisch' },
  warning:  { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-200 dark:border-amber-900', label: 'Warnung' },
  info:     { icon: Info, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-950/30', border: 'border-blue-200 dark:border-blue-900', label: 'Info' },
};

const CODE_LABELS = {
  SCOPE_MISMATCH: 'Scope stimmt nicht mit Rolle überein',
  MISSING_LOCATION_IDS: 'Keine Standorte zugewiesen',
  MISSING_BRANCH_IDS: 'Keine Niederlassungen zugewiesen',
  ORPHAN_LOCATION: 'Nicht mehr existierende Standorte',
  ORPHAN_BRANCH: 'Nicht mehr existierende Niederlassungen',
  UNKNOWN_ROLE: 'Unbekannte Rolle',
  ORPHAN_USER: 'Berechtigung ohne User',
};

export default function PermissionsAudit({ embedded = false }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healing, setHealing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await permissionsAuditAPI.getAudit();
      setAudit(data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Audit konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleHeal = async () => {
    if (!window.confirm('Alle SCOPE_MISMATCH-Fehler jetzt automatisch reparieren?\n\nDer scope_type wird für betroffene User auf den Wert der zugeordneten Rolle gesetzt.')) return;
    setHealing(true);
    try {
      const { data } = await permissionsAuditAPI.heal();
      toast.success(`${data.healed_count} Berechtigung(en) repariert.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reparatur fehlgeschlagen');
    } finally {
      setHealing(false);
    }
  };

  const summary = audit?.summary;
  const hasIssues = summary && summary.users_with_issues > 0;
  const hasScopeMismatch = summary?.by_code?.SCOPE_MISMATCH > 0;

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6" data-testid="permissions-audit-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Shield className="h-7 w-7 text-emerald-600" />
              Berechtigungen-Selbsttest
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Prüft alle Rollen-Zuweisungen auf Konsistenz mit den System-Rollen
              und den referenzierten Standorten/Niederlassungen.
            </p>
          </div>
          <div className="flex gap-2">
            {hasScopeMismatch && (
              <Button
                variant="default"
                onClick={handleHeal}
                disabled={healing || loading}
                data-testid="permissions-audit-heal-btn"
              >
                <Wrench className={`h-4 w-4 mr-2 ${healing ? 'animate-spin' : ''}`} />
                Scope-Fehler reparieren
              </Button>
            )}
            <Button
              variant="outline"
              onClick={load}
              disabled={loading}
              data-testid="permissions-audit-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryCard
                label="User mit Berechtigung"
                value={summary?.total_users_with_permissions ?? 0}
                tone="neutral"
              />
              <SummaryCard
                label="User mit Problemen"
                value={summary?.users_with_issues ?? 0}
                tone={hasIssues ? 'critical' : 'ok'}
              />
              <SummaryCard
                label="Kritisch"
                value={summary?.by_severity?.critical ?? 0}
                tone={summary?.by_severity?.critical > 0 ? 'critical' : 'ok'}
              />
              <SummaryCard
                label="Warnungen"
                value={summary?.by_severity?.warning ?? 0}
                tone={summary?.by_severity?.warning > 0 ? 'warning' : 'ok'}
              />
            </div>

            {/* All-clear Banner */}
            {!hasIssues && (
              <Alert className="bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertTitle>Alles in Ordnung</AlertTitle>
                <AlertDescription>
                  Alle Berechtigungs-Zuweisungen sind konsistent. Kein Handlungsbedarf.
                </AlertDescription>
              </Alert>
            )}

            {/* Issue Breakdown */}
            {summary?.by_code && Object.keys(summary.by_code).length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Problem-Typen</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(summary.by_code).map(([code, count]) => (
                      <Badge
                        key={code}
                        variant="outline"
                        className="text-xs"
                        data-testid={`audit-code-${code}`}
                      >
                        <span className="font-mono text-muted-foreground mr-1">{code}</span>
                        <span className="font-semibold">{count}</span>
                        <span className="mx-1 text-muted-foreground">·</span>
                        <span className="text-muted-foreground">{CODE_LABELS[code] || code}</span>
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Per-User Findings */}
            {hasIssues && (
              <Card>
                <CardHeader>
                  <CardTitle>Betroffene User</CardTitle>
                  <CardDescription>
                    {audit.users.length} User mit Problemen — sortiert nach Schweregrad.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3" data-testid="audit-user-list">
                  {[...audit.users]
                    .sort((a, b) => {
                      const sev = (u) => u.issues.some(i => i.severity === 'critical') ? 0 : 1;
                      return sev(a) - sev(b);
                    })
                    .map((u) => (
                      <UserFindingCard
                        key={u.user_id}
                        user={u}
                        locationMap={audit.location_map}
                        branchMap={audit.branch_map}
                      />
                    ))}
                </CardContent>
              </Card>
            )}

            <p className="text-xs text-muted-foreground text-right">
              Generiert: {audit?.generated_at && format(parseISO(audit.generated_at), 'dd.MM.yyyy HH:mm:ss', { locale: de })} Uhr
            </p>
          </>
        )}
      </div>
    </MaybeLayout>
  );
}

const SummaryCard = ({ label, value, tone }) => {
  const toneClasses = {
    neutral: 'bg-muted/30',
    ok: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900',
    warning: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900',
    critical: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900',
  };
  return (
    <Card className={toneClasses[tone] || ''}>
      <CardContent className="p-4">
        <div className="text-3xl font-bold">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
      </CardContent>
    </Card>
  );
};

const UserFindingCard = ({ user, locationMap, branchMap }) => {
  const criticalCount = user.issues.filter(i => i.severity === 'critical').length;
  const topSeverity = criticalCount > 0 ? 'critical' : 'warning';
  const sev = SEVERITY_CONFIG[topSeverity];

  return (
    <div
      className={`border rounded-lg p-4 ${sev.bg} ${sev.border}`}
      data-testid={`audit-user-${user.user_id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <sev.icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${sev.color}`} />
        <div className="flex-1 min-w-0 space-y-2">
          {/* User-Kopfzeile */}
          <div className="flex items-start gap-2 flex-wrap">
            <div>
              <div className="font-semibold">{user.name || '(unbekannt)'}</div>
              <div className="text-xs text-muted-foreground">{user.email}</div>
            </div>
            {user.role_name && (
              <Badge variant="outline" className="text-[10px]">
                {user.role_name}
              </Badge>
            )}
          </div>

          {/* Scope-Info */}
          {user.expected_scope && (
            <div className="text-xs text-muted-foreground">
              <span className="font-mono">scope_type:</span>{' '}
              {user.stored_scope && user.stored_scope !== user.expected_scope ? (
                <>
                  <span className="line-through">{user.stored_scope}</span>
                  {' → '}
                  <span className="text-emerald-700 dark:text-emerald-400 font-semibold">{user.expected_scope}</span>
                </>
              ) : (
                <span className="font-semibold">{user.effective_scope}</span>
              )}
            </div>
          )}

          {/* Zugewiesene Standorte/Niederlassungen */}
          {user.location_ids?.length > 0 && (
            <div className="text-xs">
              <span className="text-muted-foreground">Standorte: </span>
              {user.location_ids.map((lid) => (
                <Badge
                  key={lid}
                  variant="outline"
                  className={`text-[10px] mr-1 ${!locationMap[lid] ? 'border-red-400 text-red-700' : ''}`}
                  title={lid}
                >
                  {locationMap[lid] || `⚠ ${lid.slice(0, 8)}…`}
                </Badge>
              ))}
            </div>
          )}
          {user.branch_ids?.length > 0 && (
            <div className="text-xs">
              <span className="text-muted-foreground">Niederlassungen: </span>
              {user.branch_ids.map((bid) => (
                <Badge
                  key={bid}
                  variant="outline"
                  className={`text-[10px] mr-1 ${!branchMap[bid] ? 'border-red-400 text-red-700' : ''}`}
                  title={bid}
                >
                  {branchMap[bid] || `⚠ ${bid.slice(0, 8)}…`}
                </Badge>
              ))}
            </div>
          )}

          {/* Issue-Liste */}
          <ul className="space-y-1 pt-1">
            {user.issues.map((iss, idx) => {
              const issSev = SEVERITY_CONFIG[iss.severity] || SEVERITY_CONFIG.info;
              return (
                <li key={`${iss.code}-${idx}`} className="flex items-start gap-2 text-xs">
                  <issSev.icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${issSev.color}`} />
                  <div>
                    <span className="font-mono text-[10px] text-muted-foreground mr-1">
                      {iss.code}
                    </span>
                    <span>{iss.message}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};
